// src/pages/calidad/AuditoriasReporte.jsx
import { useEffect, useState } from 'react';
import api from '../../services/api';
import DOMPurify from 'dompurify';

export default function AuditoriasReporte() {
  const [historial, setHistorial] = useState([]);
  const [tendencia, setTendencia] = useState([]);
  const [expandidoId, setExpandidoId] = useState(null);
  const [detalle, setDetalle] = useState({});
  const [cargando, setCargando] = useState(true);

  async function cargar() {
    setCargando(true);
    try {
      const [resHistorial, resReporte] = await Promise.all([
        api.get('/api/calidad/auditorias'),
        api.get('/api/calidad/auditorias/reporte'),
      ]);
      setHistorial(resHistorial.data.data);
      setTendencia(resReporte.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  async function toggleExpandir(id) {
    if (expandidoId === id) { setExpandidoId(null); return; }
    setExpandidoId(id);
    if (!detalle[id]) {
      try {
        const res = await api.get(`/api/calidad/auditorias/${id}`);
        setDetalle((prev) => ({ ...prev, [id]: res.data.data }));
      } catch (err) {
        console.error(err);
      }
    }
  }

  async function abrirPdf(id) {
    try {
      const res = await api.get(`/api/calidad/auditorias/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      console.error(err);
    }
  }

  const ultima = historial[0];
  const anterior = historial[1];
  const maxNC = Math.max(1, ...tendencia.map((t) => t.num_no_conformidades));

  if (cargando) return <div style={styles.contenedor}>Cargando...</div>;

  return (
    <div style={styles.contenedor} className="contenedor-captura">
      <h1 style={styles.titulo}>5.5 Resultados de Auditoría</h1>
      <p style={styles.subtitulo}>Total de no conformidades por auditoría. No tiene meta definida — es un indicador de tendencia.</p>

      <div style={styles.tarjetasResumen}>
        <div style={styles.tarjetaKpi}>
          <div style={styles.kpiLabel}>Última auditoría {ultima ? `(${ultima.fecha_inicio})` : ''}</div>
          <div style={styles.kpiValor}>{ultima ? ultima.num_no_conformidades : '—'}</div>
          <div style={styles.kpiDetalle}>no conformidades{ultima ? ` · ${ultima.num_observaciones} observaciones` : ''}</div>
        </div>
        <div style={styles.tarjetaKpi}>
          <div style={styles.kpiLabel}>Auditoría anterior {anterior ? `(${anterior.fecha_inicio})` : ''}</div>
          <div style={styles.kpiValor}>{anterior ? anterior.num_no_conformidades : '—'}</div>
          <div style={styles.kpiDetalle}>no conformidades</div>
        </div>
      </div>

      <div style={styles.tarjeta}>
        <div style={styles.seccionTitulo}>Tendencia (últimas {tendencia.length} auditorías)</div>
        <div style={styles.grafica}>
          {tendencia.map((t) => (
            <div key={t.id} style={styles.barraContenedor}>
              <div style={{ ...styles.barra, height: `${(t.num_no_conformidades / maxNC) * 100}%` }} />
              <div style={styles.barraValor}>{t.num_no_conformidades}</div>
              <div style={styles.barraFecha}>{t.fecha_inicio}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.seccionTitulo2}>Historial de auditorías</div>
      <div style={styles.tarjeta}>
        {historial.length === 0 ? (
          <div style={styles.vacio}>Sin auditorías capturadas todavía.</div>
        ) : (
          historial.map((a) => (
            <div key={a.id}>
              <div style={styles.filaHistorial} onClick={() => toggleExpandir(a.id)}>
                <div>
                  <div style={styles.nombreHistorial}>{a.fecha_inicio}{a.fecha_fin ? ` a ${a.fecha_fin}` : ''}</div>
                  {a.area_proceso && (
                    <div
                      style={{ ...styles.detalleHistorial, fontWeight: 600 }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.area_proceso) }}
                    />
                  )}
                  <div style={styles.detalleHistorial}>Auditor líder: {a.auditor_lider || '—'}</div>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={styles.badgeNC}>{a.num_no_conformidades} NC</span>
                  <span style={styles.badgeObs}>{a.num_observaciones} obs.</span>
                  {a.tiene_pdf && (
                    <button
                      onClick={(e) => { e.stopPropagation(); abrirPdf(a.id); }}
                      style={styles.linkPdf}
                    >
                      PDF
                    </button>
                  )}
                  <span style={styles.flecha}>{expandidoId === a.id ? '▲' : '▼'}</span>
                </div>
              </div>

              {expandidoId === a.id && detalle[a.id] && (
                <div style={styles.detalleExpandido}>
                  {detalle[a.id].area_proceso && (
                    <div>
                      <div style={styles.subtituloDetalle}>Área o proceso</div>
                      <div style={styles.htmlDetalle} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detalle[a.id].area_proceso) }} />
                    </div>
                  )}

                  {detalle[a.id].resumen && (
                    <div>
                      <div style={styles.subtituloDetalle}>Resumen</div>
                      <div style={styles.htmlDetalle} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detalle[a.id].resumen) }} />
                    </div>
                  )}

                  {detalle[a.id].alcance && (
                    <div>
                      <div style={styles.subtituloDetalle}>Alcance / Criterios de auditoría</div>
                      <div style={styles.htmlDetalle} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detalle[a.id].alcance) }} />
                    </div>
                  )}
                  {detalle[a.id].objetivo && (
                    <div>
                      <div style={styles.subtituloDetalle}>Objetivo</div>
                      <div style={styles.htmlDetalle} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detalle[a.id].objetivo) }} />
                    </div>
                  )}

                  {detalle[a.id].no_conformidades.length > 0 && (
                    <>
                      <div style={styles.subtituloDetalle}>No Conformidades</div>
                      {detalle[a.id].no_conformidades.map((nc) => (
                        <div key={nc.numero} style={styles.filaNCDetalle}>
                          <strong>{nc.numero}. {nc.requisito}</strong>
                          <div style={styles.htmlDetalle} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(nc.situacion_actual) }} />
                        </div>
                      ))}
                    </>
                  )}

                  {detalle[a.id].observaciones.length > 0 && (
                    <>
                      <div style={styles.subtituloDetalle}>Observaciones</div>
                      {detalle[a.id].observaciones.map((ob) => (
                        <div key={ob.numero} style={styles.htmlDetalle} dangerouslySetInnerHTML={{ __html: `${ob.numero}. ${DOMPurify.sanitize(ob.descripcion)}` }} />
                      ))}
                    </>
                  )}

                  {detalle[a.id].oportunidades.length > 0 && (
                    <>
                      <div style={styles.subtituloDetalle}>Oportunidades de Mejora</div>
                      {detalle[a.id].oportunidades.map((op) => (
                        <div key={op.numero} style={styles.htmlDetalle} dangerouslySetInnerHTML={{ __html: `${op.numero}. ${DOMPurify.sanitize(op.descripcion)}` }} />
                      ))}
                    </>
                  )}

                  {detalle[a.id].fortalezas.length > 0 && (
                    <>
                      <div style={styles.subtituloDetalle}>Fortalezas</div>
                      {detalle[a.id].fortalezas.map((f) => (
                        <div key={f.numero} style={styles.htmlDetalle} dangerouslySetInnerHTML={{ __html: `${f.numero}. ${DOMPurify.sanitize(f.descripcion)}` }} />
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42', maxWidth: '820px' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '4px' },
  subtitulo: { fontSize: '12px', color: '#8A7690', marginBottom: '18px' },
  tarjetasResumen: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '16px' },
  tarjetaKpi: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)' },
  kpiLabel: { fontSize: '11px', color: '#8A7690', marginBottom: '6px', fontWeight: 600 },
  kpiValor: { fontSize: '30px', fontWeight: 700, color: '#2A1830' },
  kpiDetalle: { fontSize: '11px', color: '#8A7690', marginTop: '4px' },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', marginBottom: '16px' },
  seccionTitulo: { fontFamily: 'Playfair Display, serif', fontSize: '15px', color: '#2A1830', marginBottom: '14px' },
  seccionTitulo2: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#2A1830', marginBottom: '10px' },
  grafica: { display: 'flex', gap: '16px', alignItems: 'flex-end', height: '140px', paddingTop: '10px', overflowX: 'auto' },
  barraContenedor: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 40px', height: '100%', justifyContent: 'flex-end' },
  barra: { width: '28px', background: '#6A2C75', borderRadius: '4px 4px 0 0', minHeight: '4px' },
  barraValor: { fontSize: '12px', fontWeight: 700, color: '#2A1830', marginTop: '4px' },
  barraFecha: { fontSize: '9px', color: '#8A7690', marginTop: '2px', textAlign: 'center' },
  vacio: { textAlign: 'center', color: '#8A7690', fontSize: '13px', padding: '10px' },
  filaHistorial: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '12px 0', borderBottom: '1px solid #F0E9E0', cursor: 'pointer', flexWrap: 'wrap' },
  nombreHistorial: { fontSize: '13px', fontWeight: 700, color: '#2A1830' },
  detalleHistorial: { fontSize: '11px', color: '#8A7690', marginTop: '2px' },
  badgeNC: { fontSize: '11px', fontWeight: 700, background: '#FDF3F1', color: '#B23A48', borderRadius: '6px', padding: '3px 8px' },
  badgeObs: { fontSize: '11px', fontWeight: 700, background: '#FFF6E9', color: '#B2662A', borderRadius: '6px', padding: '3px 8px' },
  linkPdf: { fontSize: '11px', fontWeight: 700, color: '#6A2C75', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 },
  flecha: { fontSize: '10px', color: '#8A7690' },
  detalleExpandido: { padding: '12px 0 16px', borderBottom: '1px solid #F0E9E0', display: 'flex', flexDirection: 'column', gap: '10px' },
  subtituloDetalle: { fontSize: '12px', fontWeight: 700, color: '#6A2C75', marginTop: '6px' },
  filaNCDetalle: { display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '12px' },
  textoDetalle: { fontSize: '12px', color: '#3A2E42', margin: 0 },
  htmlDetalle: { fontSize: '12px', color: '#3A2E42', lineHeight: 1.5 },
};